import { useState, useEffect, useRef } from "react";
import * as XLSX from "xlsx";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Toast } from "primereact/toast";
import { Dialog } from "primereact/dialog";

export default function TrackingCustomer() {
  const [previousData, setPreviousData] = useState([]);
  const [currentData, setCurrentData] = useState([]);
  const [comparisonRows, setComparisonRows] = useState([]);
  const [dynamicColumns, setDynamicColumns] = useState([]);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [previewData, setPreviewData] = useState([]);
  const [previewColumns, setPreviewColumns] = useState([]);
  const [previewTitle, setPreviewTitle] = useState("");

  const [showHeadingDialog, setShowHeadingDialog] = useState(false);
  const sampleFiles=[
    {
      filePath:"https://schooldevapi.dosystemsinc.com/images/server/upload/aiPocSampleFiles/TrackingCustomer200.xlsx",
      fileName:"Tracking Customer Surveillance Sample File"
    },
     
  ]

  const toast = useRef(null);

  const normalize = (val) => {
    if (val === null || val === undefined) return "";
    if (typeof val === "string") return val.trim();
    if (!isNaN(val)) return Number(val);
    return val;
  };

  const readExcel = (file, setState, storageKey) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const wb = XLSX.read(e.target.result, { type: "binary" });
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(sheet);

      setState(json);
      localStorage.setItem(storageKey, JSON.stringify(json));

      toast.current.show({
        severity: "success",
        summary: "File Loaded",
        detail: `${json.length} records loaded`,
        life: 2000,
      });
    };
    reader.readAsBinaryString(file);
  };

  const loadDefaultPreviousExcel = async () => {
    try {
      const response = await fetch("/default_previous200.xlsx");
      const arrayBuffer = await response.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: "array" });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(sheet);

      setPreviousData(json);
      localStorage.setItem("previous_excel", JSON.stringify(json));

      toast.current.show({
        severity: "success",
        summary: "Default Previous Excel Loaded",
        detail: `${json.length} records loaded`,
        life: 2000,
      });
    } catch (error) {
      toast.current.show({
        severity: "error",
        summary: "Failed to Load",
        detail: "Could not load file from public folder",
        life: 3000,
      });
    }
  };

  useEffect(() => {
    const prev = localStorage.getItem("previous_excel");

    if (prev) {
      setPreviousData(JSON.parse(prev));
    } else {
      loadDefaultPreviousExcel();
    }
  }, []);

  const handlePreview = (data, title) => {
    if (!data.length) {
      toast.current.show({
        severity: "warn",
        summary: "No Data",
        detail: "Upload file first",
      });
      return;
    }
    setPreviewData(data);
    setPreviewColumns(Object.keys(data[0]));
    setPreviewTitle(title);
    setShowPreviewDialog(true);
  };

  const downloadComparison = () => {
    if (!comparisonRows.length) {
      toast.current.show({
        severity: "warn",
        summary: "No Data",
        detail: "Run comparison first",
      });
      return;
    }

    const ws = XLSX.utils.json_to_sheet(comparisonRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Comparison");
    XLSX.writeFile(wb, `comparison_${Date.now()}.xlsx`);

    toast.current.show({
      severity: "success",
      summary: "Downloaded",
      detail: "Comparison Excel downloaded",
    });
  };

  const compareExcels = () => {
    if (!previousData.length || !currentData.length) {
      toast.current.show({
        severity: "warn",
        summary: "Missing File",
        detail: "Upload current excel first",
      });
      return;
    }

    const result = [];
    const changedColumns = new Set();

    const newMap = {};
    currentData.forEach((r) => {
      if (!newMap[r.CustomerID]) newMap[r.CustomerID] = [];
      newMap[r.CustomerID].push(r);
    });

    const oldMap = {};
    previousData.forEach((r) => {
      if (!oldMap[r.CustomerID]) oldMap[r.CustomerID] = [];
      oldMap[r.CustomerID].push(r);
    });

    const allIds = new Set([
      ...previousData.map((x) => x.CustomerID),
      ...currentData.map((x) => x.CustomerID),
    ]);

    allIds.forEach((id) => {
      const oldList = oldMap[id] || [];
      const newList = newMap[id] || [];

      if (oldList.length > 1) {
        result.push({ CustomerID: id, Status: "DUPLICATE_IN_OLD" });
        changedColumns.add("Status");
        return;
      }

      if (newList.length > 1) {
        result.push({ CustomerID: id, Status: "DUPLICATE_IN_NEW" });
        changedColumns.add("Status");
        return;
      }

      const oldRow = oldList[0];
      const newRow = newList[0];

      if (oldRow && !newRow) {
        result.push({ CustomerID: id, Status: "DELETED" });
        changedColumns.add("Status");
        return;
      }

      if (!oldRow && newRow) {
        result.push({ CustomerID: id, Status: "NEW_CUSTOMER" });
        changedColumns.add("Status");
        return;
      }

      let modified = false;
      const compareRow = { CustomerID: id };
      const fields = Object.keys(oldRow).filter((f) => f !== "CustomerID");

      fields.forEach((field) => {
        const oldVal = normalize(oldRow[field]);
        const newVal = normalize(newRow[field]);
        const isNum = typeof oldVal === "number" || typeof newVal === "number";
        const changed = isNum ? oldVal !== newVal : oldVal != newVal;

        if (changed) {
          modified = true;
          compareRow[`${field}_old`] = oldVal;
          compareRow[`${field}_new`] = newVal;

          changedColumns.add(`${field}_old`);
          changedColumns.add(`${field}_new`);
        }
      });

      if (modified) {
        compareRow.Status = "MODIFIED";
        result.push(compareRow);
      } else {
        result.push({ CustomerID: id, Status: "NO_CHANGE" });
      }

      changedColumns.add("Status");
    });

    setComparisonRows(result);
    setDynamicColumns(["CustomerID", ...Array.from(changedColumns)]);
  };

  const rowClassName = (row) => {
    return (
      {
        DELETED: "row-deleted",
        MODIFIED: "row-modified",
        NEW_CUSTOMER: "row-new",
        DUPLICATE_IN_OLD: "row-dupe",
        DUPLICATE_IN_NEW: "row-dupe",
        NO_CHANGE: "",
      }[row.Status] || ""
    );
  };

  return (
    <div style={{ maxHeight: "90vh", overflowY: "auto", paddingRight: 10 }}>
      <style>
        {`
          .p-datatable table { border-collapse: collapse !important; }
          .p-datatable td, .p-datatable th { border: 1px solid #d1d5db !important; }

          .row-deleted { background: #f5c8c8 !important; }
          .row-modified { background: #f3e975 !important; }
          .row-new { background: #c8f4d8 !important; }
          .row-dupe { background: #f0cc9c !important; }
        `}
      </style>



      <div style={styles.wrapper}>
        <Toast ref={toast} />

        {/* 🔥 HEADING DETAILS POPUP */}
        <Dialog
          header="Tracking Customer Surveillance — Details"
          visible={showHeadingDialog}
          style={{ width: "60vw" }}
          onHide={() => setShowHeadingDialog(false)}
        >
          <div style={styles.headingDialogText}>
            <p><strong>Tracking #:</strong> 24</p>
            <p><strong>Area:</strong> GP</p>
            <p><strong>Contact:</strong> Angelo Triantafilou</p>

            <p style={{ marginTop: 10 }}>
              Issue: Per FSS rules, every price change must be reported within 15 days.
            </p>

            <ul>
              <li>AI extracts & compares data</li>
              <li>Runs weekly checks</li>
              <li>LLM-based automation</li>
              <li>Saves 40–60 hours/month</li>
            </ul>

            <hr />
            {/* ---- Sample File Download Button ---- */}
              <div style={{ marginTop: 20 }}>
              {sampleFiles.map((item, index) => {
                return (
                  <a
                    key={index}
                    href={item.filePath}
                    download
                    style={{
                      padding: "10px 16px",
                      background: "#12225f",
                      color: "#fff",
                      borderRadius: "6px",
                      textDecoration: "none",
                      fontWeight: "bold",
                      display: "block",      // <-- makes each button appear on a new line
                      marginBottom: "10px",  // <-- spacing between buttons
                      width: "fit-content"   // <-- button fits to text size
                    }}
                  >
                    {`${item.fileName} ${index + 1}` || `Sample File ${index + 1}`}
                  </a>
                );
              })}
            </div>
            <div style={{ marginTop: 20 }}>
              <h4 style={{ marginBottom: 10 }}>Process</h4>

              <ol style={{ paddingLeft: 20, lineHeight: "1.6" }}>
                    <li><strong>Download the Sample File from  above Buttons</strong></li>

                <li><strong>Upload File at "Current Excel"</strong></li>
                <li><strong>click "Compare" button</strong></li>
                <li><strong>View the Compared Results</strong></li>
              </ol>
            </div>

          </div>
        </Dialog>

        {/* 🚀 NEW INDUSTRY-STANDARD HEADER LAYOUT */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: "white",
            padding: "15px",
            borderRadius: "10px",
            marginBottom: "12px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.06)",
            gap: "20px",
          }}
        >
          {/* LEFT: UPLOAD SECTION */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              background: "#f8fafc",
              padding: "10px 12px",
              borderRadius: 8,
              border: "1px dashed #cbd5e1",
              minWidth: "260px",
            }}
          >
            <label style={{ fontWeight: 600 }}>Current Excel</label>

            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={(e) =>
                readExcel(e.target.files[0], setCurrentData, "current_excel")
              }
              style={{
                width: "100%",
                padding: 6,
                borderRadius: 6,
                marginTop: 6,
                background: "white",
              }}
            />

            {currentData.length > 0 && (
              <div
                style={{
                  marginTop: 8,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span
                  style={{
                    background: "#1e3a8a",
                    color: "white",
                    padding: "4px 10px",
                    borderRadius: 8,
                  }}
                >
                  {currentData.length} rows
                </span>

                <button
                  style={{
                    background: "#1e3a8a",
                    color: "white",
                    padding: "4px 12px",
                    borderRadius: 6,
                  }}
                  onClick={() =>
                    handlePreview(currentData, "Current Excel Preview")
                  }
                >
                  Preview
                </button>
              </div>
            )}
          </div>

          {/* CENTER: MAIN HEADING WITH PRIME ICON */}
          <div
            style={{
              flexGrow: 1,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <h2 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>
                Tracking Customer Surveillance
              </h2>

              <i
                className="pi pi-eye"
                style={{
                  fontSize: "22px",
                  cursor: "pointer",
                  color: "#1e3a8a",
                }}
                onClick={() => setShowHeadingDialog(true)}
              ></i>
            </div>
          </div>

          {/* RIGHT: ACTION BUTTONS */}
          <div style={{ display: "flex", gap: 10 }}>
            <button
              style={{
                padding: "10px 16px",
                background: "#1e40af",
                color: "white",
                borderRadius: 6,
                whiteSpace: "nowrap",
              }}
              onClick={compareExcels}
            >
              Compare
            </button>

            {comparisonRows.length > 0 && (
              <button
                style={{
                  padding: "10px 16px",
                  background: "#059669",
                  color: "white",
                  borderRadius: 6,
                  whiteSpace: "nowrap",
                }}
                onClick={downloadComparison}
              >
                Download
              </button>
            )}
          </div>
        </div>

        {/* RESULTS TABLE */}
        {comparisonRows.length > 0 && (
          <div style={styles.resultsSection}>
            <div style={styles.resultsHeader}>
              <h3 style={styles.resultsTitle}>Results</h3>
              <span style={styles.recordCount}>{comparisonRows.length}</span>
            </div>
      {/* 🔵 COLOR CODING LEGEND */}
        <div
          style={{
            display: "flex",
            gap: "20px",
            background: "white",
            padding: "12px 16px",
            borderRadius: 8,
            marginBottom: 12,
            alignItems: "end",
            boxShadow: "0 2px 4px rgba(0,0,0,0.08)",
            flexWrap: "wrap",
              justifyContent: "flex-end"   // <-- THIS LINE

          }}
        >
        

          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div style={{
              width: 20,
              height: 20,
              background: "#f5c8c8",
              border: "1px solid #d1d5db",
              borderRadius: 4
            }} />
            <span>Deleted</span>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div style={{
              width: 20,
              height: 20,
              background: "#f3e975",
              border: "1px solid #d1d5db",
              borderRadius: 4
            }} />
            <span>Modified</span>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div style={{
              width: 20,
              height: 20,
              background: "#c8f4d8",
              border: "1px solid #d1d5db",
              borderRadius: 4
            }} />
            <span>New Customer</span>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div style={{
              width: 20,
              height: 20,
              background: "#f0cc9c",
              border: "1px solid #d1d5db",
              borderRadius: 4
            }} />
            <span>Duplicate</span>
          </div>
        </div>
            <DataTable
              value={comparisonRows}
              scrollable
              paginator
              stripedRows
              rows={20}
              scrollHeight="70vh"
              style={styles.dataTable}
              rowClassName={rowClassName}
            >
              {dynamicColumns.map((col) => (
                <Column key={col} field={col} header={col} />
              ))}
            </DataTable>
          </div>
        )}

        {/* PREVIEW POPUP */}
        <Dialog
          header={previewTitle}
          visible={showPreviewDialog}
          style={{ width: "90vw" }}
          maximizable
          onHide={() => setShowPreviewDialog(false)}
        >
          <DataTable
            value={previewData}
            scrollable
            paginator
            rows={20}
            scrollHeight="65vh"
          >
            {previewColumns.map((col) => (
              <Column key={col} field={col} header={col} />
            ))}
          </DataTable>
        </Dialog>
      </div>
    </div>
  );
}

/* 🔥 STYLES */
const styles = {
  wrapper: {
    margin: "5px",
    padding: "5px",
    background: "#f3f4f6",
  },

  headingDialogText: {
    fontSize: 15,
    lineHeight: "1.6",
  },

  resultsSection: {
    background: "white",
    borderRadius: 10,
    padding: 15,
  },

  resultsHeader: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: 10,
  },

  resultsTitle: { fontSize: 18 },

  recordCount: {
    padding: "4px 10px",
    background: "#e0f2fe",
    borderRadius: 12,
  },

  dataTable: {
    borderRadius: 8,
  },
};
